import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { toast } from 'react-toastify'
import { useCallback, useEffect, useState } from 'react'
import {
  deleteIssuedDietPlan,
  listIssuedDietPlansForEvent,
} from 'src/mentor/services/dietPlanService'
import DietPlanEditorDrawer from './DietPlanEditorDrawer'
import DietPlanTemplatePicker from './DietPlanTemplatePicker'
import { formatQuantity, languageLabel } from './dietPlanLabels'

/**
 * Drop-in panel that lists every diet plan issued during the current event and exposes
 * "Add new plan" (template picker) and "Edit" / "Delete" per existing plan. Designed to render
 * inside the Diet Plan EMR section in the nutrition therapy report.
 */
export default function IssuedDietPlansPanel({ bookedEventId, patientName, defaultLanguage = 'en' }) {
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [editorState, setEditorState] = useState({ open: false, template: null, existing: null, language: defaultLanguage })

  const refresh = useCallback(async () => {
    if (!bookedEventId) return
    setLoading(true)
    try {
      const rows = await listIssuedDietPlansForEvent(bookedEventId)
      setPlans(rows)
    } catch (err) {
      console.error('Failed to load issued diet plans', err)
    } finally {
      setLoading(false)
    }
  }, [bookedEventId])

  useEffect(() => {
    refresh()
  }, [refresh])

  const onPickTemplate = (template, language) => {
    setPickerOpen(false)
    setEditorState({ open: true, template, existing: null, language })
  }

  const onEditExisting = (plan) => {
    setEditorState({ open: true, template: null, existing: plan, language: plan?.language ?? defaultLanguage })
  }

  const onSavedPlan = () => {
    refresh()
  }

  const onDelete = async (plan) => {
    if (!plan?.id) return
    if (!window.confirm('Delete this diet plan? It will be hidden from the patient.')) return
    try {
      await deleteIssuedDietPlan(plan.id)
      toast.success('Diet plan deleted')
      refresh()
    } catch (err) {
      console.error('Failed to delete diet plan', err)
      toast.error('Failed to delete')
    }
  }

  return (
    <Box sx={{ marginTop: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1f2937' }}>
          Issued diet plans ({plans.length})
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => setPickerOpen(true)}
          disabled={!bookedEventId}
        >
          Pick template
        </Button>
      </Stack>

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Stack>
      )}

      {!loading && plans.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No diet plans issued for this consultation yet. Pick a template to start.
        </Typography>
      )}

      <Stack spacing={1.5}>
        {plans.map((plan) => (
          <PlanRow key={plan.id} plan={plan} onEdit={() => onEditExisting(plan)} onDelete={() => onDelete(plan)} />
        ))}
      </Stack>

      <DietPlanTemplatePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPickTemplate={onPickTemplate}
        initialLanguage={defaultLanguage}
      />

      <DietPlanEditorDrawer
        open={editorState.open}
        onClose={() => setEditorState((prev) => ({ ...prev, open: false }))}
        bookedEventId={bookedEventId}
        patientName={patientName}
        templateDraft={editorState.template}
        existingPlan={editorState.existing}
        defaultLanguage={editorState.language}
        onSaved={onSavedPlan}
      />
    </Box>
  )
}

function PlanRow({ plan, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false)
  const meals = plan.meals ?? []
  return (
    <Box
      sx={{
        border: '1px solid rgba(99, 102, 241, 0.2)',
        borderRadius: 1.5,
        overflow: 'hidden',
        background: '#fff',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1.25, background: 'rgba(99, 102, 241, 0.04)' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
            {plan.title || 'Diet plan'}
            {plan.language && (
              <Chip size="small" label={languageLabel(plan.language)} variant="outlined" />
            )}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 0.25, flexWrap: 'wrap' }}>
            {plan.approxCaloriesPerDay != null && (
              <Typography variant="caption" color="text.secondary">
                ~{plan.approxCaloriesPerDay} kcal/day
              </Typography>
            )}
            {plan.durationDays != null && (
              <Typography variant="caption" color="text.secondary">
                {plan.durationDays} days
              </Typography>
            )}
            {plan.reviewAfter && (
              <Typography variant="caption" color="text.secondary">
                review after {plan.reviewAfter}
              </Typography>
            )}
          </Stack>
        </Box>
        <Tooltip title={expanded ? 'Hide plan' : 'Preview plan'}>
          <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={onEdit}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={onDelete}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {expanded && (
        <Box sx={{ px: 2, py: 1.5 }}>
          {plan.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {plan.description}
            </Typography>
          )}
          {meals.map((meal, i) => (
            <Box key={`meal-${i}`} sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {meal.title || meal.mealSlot}
                {meal.suggestedTime && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    @ {meal.suggestedTime}
                  </Typography>
                )}
                {meal.approxCalories != null && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    · {meal.approxCalories} kcal
                  </Typography>
                )}
              </Typography>
              {(meal.options ?? []).map((opt, oi) => (
                <Box key={`opt-${oi}`} sx={{ pl: 2, mt: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    • {opt.displayName}
                  </Typography>
                  {(opt.items ?? []).length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 2, display: 'block' }}>
                      {(opt.items ?? [])
                        .map((it) => `${it.displayName} (${formatQuantity(it.quantity)})`)
                        .join(', ')}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ))}
          {(plan.generalGuidelines ?? []).length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151' }}>
                Guidelines
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0 }}>
                {plan.generalGuidelines.map((g, i) => (
                  <li key={`g-${i}`}>
                    <Typography variant="caption" color="text.secondary">
                      {g}
                    </Typography>
                  </li>
                ))}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  )
}
